import { EmptyStateTable } from '@components'

import { Color, Text } from '@billy/design-system'
import { Table } from '@shinetools/lumen-react'
import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { EXPERT_PROFIT_AND_LOSS_TABLE_MIN_WIDTH } from '../../constants/expertProfitAndLossColumnWidths'
import { useExpertProfitAndLossColumns } from '../../hooks/useExpertProfitAndLossColumns'
import type { ExpertProfitAndLossRow, ExpertProfitAndLossTableProps } from '../../types/expertProfitAndLossView'
import { ExpertProfitAndLossSearchEmptyState } from '../ExpertProfitAndLossSearchEmptyState'
import * as Styled from './styles'

export const ExpertProfitAndLossTable = ({
  isError,
  isLoading,
  periods,
  periodType,
  rows,
  searchTerm = '',
}: ExpertProfitAndLossTableProps): ReactElement => {
  const { t } = useTranslation()
  const columns = useExpertProfitAndLossColumns(periods, periodType, searchTerm)
  const hasData = rows.length > 0
  const isSearchActive = searchTerm.trim().length > 0

  const tableHeader = (
    <Table.Header>
      <Table.Row>
        {columns.map((column) => (
          <Table.HeaderCell align={column.align} key={column.id} width={column.width}>
            {column.header}
          </Table.HeaderCell>
        ))}
      </Table.Row>
    </Table.Header>
  )

  const renderEmptyContent = (): ReactNode => {
    if (isError) {
      return (
        <Text alignment="center" color={Color.Grey800}>
          {t('expert_profit_and_loss.error.load_failed')}
        </Text>
      )
    }

    if (isSearchActive) {
      return <ExpertProfitAndLossSearchEmptyState text={t('expert_profit_and_loss.empty_state.no_data_search')} />
    }

    return (
      <Text alignment="center" color={Color.Grey800}>
        {t('expert_profit_and_loss.empty_state.no_data')}
      </Text>
    )
  }

  const lastRow = rows.at(-1)
  const footRow = lastRow?.kind === 'result' ? lastRow : undefined
  const bodyRows = footRow ? rows.slice(0, -1) : rows

  const renderRow = (row: ExpertProfitAndLossRow): ReactElement => (
    <Table.Row highlighted={row.kind === 'result' || row.kind === 'subtotal'} key={row.id}>
      {columns.map((column) => (
        <Table.Cell
          align={column.id === 'item' && (row.kind === 'result' || row.kind === 'subtotal') ? 'end' : column.align}
          highlighted={column.highlighted}
          key={column.id}
        >
          {column.Cell(row)}
        </Table.Cell>
      ))}
    </Table.Row>
  )

  if (!hasData && !isLoading) {
    return (
      <EmptyStateTable header={tableHeader} minWidth={EXPERT_PROFIT_AND_LOSS_TABLE_MIN_WIDTH}>
        {renderEmptyContent()}
      </EmptyStateTable>
    )
  }

  return (
    <Styled.ProfitAndLossTableWrapper>
      <Table compact>
        <Table.Content maxHeight="none" scrollable style={{ minWidth: EXPERT_PROFIT_AND_LOSS_TABLE_MIN_WIDTH }}>
          {tableHeader}
          {isLoading ? <Table.Skeleton columns={columns.length} /> : <Table.Body>{bodyRows.map(renderRow)}</Table.Body>}
          {!isLoading && footRow && <Table.Foot>{renderRow(footRow)}</Table.Foot>}
        </Table.Content>
      </Table>
    </Styled.ProfitAndLossTableWrapper>
  )
}
